'use client';

import { Accordion } from '@base-ui/react/accordion';
import { css } from '@repo/pandacss/styled-system/css';
import { useState } from 'react';
import { MdCheck, MdExpandLess, MdExpandMore } from 'react-icons/md';
import { shallow } from 'zustand/shallow';
import { useFilterEvents } from './useFilterEvents';

type SortOption<T> = { value: T; label: string };

type SortByMobileStoreProps<TSortValue> = <T>(
  selector: (state: { sortBy: TSortValue; handleSortBy: (val: TSortValue) => void }) => T,
  equalityFn?: (a: T, b: T) => boolean,
) => T;

type Props<TSortValue> = {
  sortOptions: readonly SortOption<TSortValue>[];
  useStore: SortByMobileStoreProps<TSortValue>;
  title?: string;
  defaultExpanded?: boolean;
  actionSection: string;
  pageIdentifier: string;
};

const headerClass = css({
  borderBottomWidth: 'thin',
  borderBottomStyle: 'solid',
  borderBottomColor: 'neutral.lighter',
});

const triggerClass = css({
  backgroundColor: 'neutral.white',
  display: 'flex',
  alignItems: 'center',
  color: '[rgba(0, 0, 0, 0.54)]',
  justifyContent: 'space-between',
  width: 'full',
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  paddingBlockStart: '4',
  paddingBlockEnd: '4',
  backgroundImage: '[none]',
  borderTopStyle: 'none',
  borderRightStyle: 'none',
  borderBottomStyle: 'none',
  borderLeftStyle: 'none',
  cursor: 'pointer',
  fontFamily: '[inherit]',
  _focusVisible: {
    outlineWidth: 'medium',
    outlineStyle: 'solid',
    outlineColor: '[#0069B9]',
  },
});

const triggerLabelClass = css({ backgroundColor: 'neutral.white', textStyle: 'filterTriggerLabel', color: 'neutral.dark' });

const panelClass = css({
  paddingTop: '0',
  paddingRight: '0',
  paddingBottom: '0',
  paddingLeft: '0',
  marginTop: '1',
});

const itemClass = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  paddingBlockStart: '1',
  paddingBlockEnd: '1',
  cursor: 'pointer',
  backgroundColor: 'neutral.white',
  backgroundImage: '[none]',
  borderTopStyle: 'none',
  borderRightStyle: 'none',
  borderBottomStyle: 'none',
  borderLeftStyle: 'none',
  width: 'full',
  fontFamily: '[inherit]',
  fontSize: 'sm',
  marginBlockStart: '1',
  marginBlockEnd: '1',
  color: 'neutral.dark',
  _hover: { backgroundColor: 'neutral.lightest' },
  _focusVisible: {
    outlineWidth: 'medium',
    outlineStyle: 'solid',
    outlineColor: '[#0069B9]',
  },
});

export const SortByMobile = <TSortValue extends string>({
  sortOptions,
  useStore,
  title = 'SORT BY',
  defaultExpanded = true,
  actionSection,
  pageIdentifier,
}: Props<TSortValue>) => {
  const { sortBy, handleSortBy } = useStore(
    ({ sortBy, handleSortBy }) => ({ sortBy, handleSortBy }),
    shallow,
  );
  const { sortBySelectEvent } = useFilterEvents({ actionSection, pageIdentifier });
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  const handleSortByChange = (value: TSortValue) => {
    setTimeout(() => {
      handleSortBy(value);
      sortBySelectEvent({
        additional_attributes: { sort_by_value: sortOptions.find(o => o.value === value)?.label },
      });
    }, 100);
  };

  return (
    <Accordion.Root
      defaultValue={defaultExpanded ? [title] : []}
      onValueChange={value => setIsOpen(value.includes(title))}
    >
      <Accordion.Item value={title}>
        <Accordion.Header className={headerClass}>
          <Accordion.Trigger className={triggerClass}>
            <span className={triggerLabelClass}>{title}</span>
            {isOpen ? (
              <MdExpandLess size={20} aria-hidden />
            ) : (
              <MdExpandMore size={20} aria-hidden />
            )}
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Panel className={panelClass}>
          {sortOptions.map(({ label, value }) => (
            <button
              key={value}
              type="button"
              className={itemClass}
              onClick={() => handleSortByChange(value)}
            >
              <span
                className={css({
                  marginBlockStart: '2',
                  marginBlockEnd: '2',
                })}
              >
                {label}
              </span>
              {sortBy === value && <MdCheck size={18} color="#008254" />}
            </button>
          ))}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion.Root>
  );
};
